/**
 * Utility to lazy-load process_flow from a flow's process_flow_url.
 * 
 * Process flows are fetched per-flow from the process_flow_url field
 * on each flow object. Once fetched, each flow's process data is
 * cached in the flowProcessFlows Redux store for global availability.
 */

/**
 * Fetches process_flow from the given process_flow_url.
 * First checks the Redux cache (flowProcessFlows) by flowId, then fetches
 * from the URL and dispatches to Redux for global caching.
 * 
 * Redux store structure: { [flowId]: { Title, Description, flow_id, process_flow: [...], ... }, ... }
 * 
 * @param {Function} dispatch - Redux dispatch
 * @param {string} flowId - The flow_id used as the Redux cache key
 * @param {string} processFlowUrl - The process_flow_url to fetch from
 * @param {object} [state] - Optional Redux state with flowProcessFlows
 * @returns {Promise<Array>} The process_flow array
 */
export async function fetchProcessFlowForFlow(dispatch, flowId, processFlowUrl, state) {
    if (!processFlowUrl || !flowId) return []

    // Check Redux cache first (keyed by flowId)
    if (state && state.flowProcessFlows && state.flowProcessFlows[flowId]) {
        const cached = state.flowProcessFlows[flowId]
        return Array.isArray(cached) ? cached : (cached && Array.isArray(cached.process_flow) ? cached.process_flow : [])
    }

    try {
        const response = await fetch(processFlowUrl)
        const data = await response.json()
        // Store full response object in Redux for global availability, keyed by flowId
        dispatch({
            type: 'GET_FLOW_PROCESS_FLOW_SUCCESS',
            payload: data,
            meta: { flowId: flowId }
        })
        const flows = Array.isArray(data) ? data : (data && Array.isArray(data.process_flow) ? data.process_flow : [])
        return flows
    } catch (error) {
        console.error('Error fetching process_flow from URL:', processFlowUrl, error)
        return []
    }
}

/**
 * Merges learn and build flows from Redux into a single array.
 * @param {object} state - Redux state containing learnPracticeFlows and buildPracticeFlows
 * @returns {Array} Merged flows array
 */
export function getMergedFlows(state) {
    const learn = state.learnPracticeFlows
    const build = state.buildPracticeFlows
    const learnFlows = learn && learn.fetched && Array.isArray(learn.flows) ? learn.flows : []
    const buildFlows = build && build.fetched && Array.isArray(build.flows) ? build.flows : []
    return [...learnFlows, ...buildFlows]
}
